import React, { useState } from 'react';
import authService from '../services/authService';
import './AuthForm.css';

const AuthForm = ({ onLogin, onRegister, variant = 'panel' }) => {
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isLoginMode = mode === 'login';
  const isForgotMode = mode === 'forgot';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submitError) {
      setSubmitError('');
    }
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email обязателен';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Введите корректный email';
    }

    if (!isForgotMode) {
      if (!formData.password) {
        newErrors.password = 'Пароль обязателен';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Минимум 6 символов';
      }
    }

    if (mode === 'register' && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      if (isForgotMode) {
        const data = await authService.forgotPassword(formData.email);
        setSuccessMessage(
          data.message ||
            'Если аккаунт с таким email существует, мы отправили инструкции по восстановлению пароля.'
        );
        return;
      }

      if (isLoginMode) {
        await onLogin(formData.email, formData.password);
      } else {
        await onRegister(formData.email, formData.password);
      }
    } catch (error) {
      setSubmitError(error.message || 'Не удалось выполнить запрос');
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setFormData({ email: '', password: '', confirmPassword: '' });
    setErrors({});
    setSubmitError('');
    setSuccessMessage('');
  };

  const title = isForgotMode ? 'Восстановление пароля' : isLoginMode ? 'Вход' : 'Регистрация';
  const subtitle = isForgotMode
    ? 'Укажите email - мы отправим ссылку для сброса пароля'
    : isLoginMode
      ? 'Войдите, чтобы открыть план и сделки'
      : 'Создайте аккаунт для доступа к платформе';

  return (
    <div className={`auth-form-shell auth-form-shell--${variant}`}>
      <div className="auth-form-header">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        {submitError && (
          <div className="auth-form-banner auth-form-banner--error" role="alert">
            {submitError}
          </div>
        )}

        {successMessage && (
          <div className="auth-form-banner auth-form-banner--success" role="status">
            {successMessage}
          </div>
        )}

        <div className="form-group">
          <label htmlFor={`auth-email-${variant}`}>Email</label>
          <input
            type="email"
            id={`auth-email-${variant}`}
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={errors.email ? 'auth-field-error' : ''}
            placeholder="you@example.com"
            disabled={isLoading}
            autoComplete="email"
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        {!isForgotMode && (
          <div className="form-group">
            <label htmlFor={`auth-password-${variant}`}>Пароль</label>
            <input
              type="password"
              id={`auth-password-${variant}`}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={errors.password ? 'auth-field-error' : ''}
              placeholder="Минимум 6 символов"
              disabled={isLoading}
              autoComplete={isLoginMode ? 'current-password' : 'new-password'}
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>
        )}

        {mode === 'register' && (
          <div className="form-group">
            <label htmlFor={`auth-confirm-${variant}`}>Подтвердите пароль</label>
            <input
              type="password"
              id={`auth-confirm-${variant}`}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={errors.confirmPassword ? 'auth-field-error' : ''}
              placeholder="Повторите пароль"
              disabled={isLoading}
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <span className="error-message">{errors.confirmPassword}</span>
            )}
          </div>
        )}

        <button type="submit" className="auth-submit-btn" disabled={isLoading}>
          {isLoading
            ? 'Загрузка...'
            : isForgotMode
              ? 'Отправить ссылку'
              : isLoginMode
                ? 'Войти'
                : 'Создать аккаунт'}
        </button>
      </form>

      <div className="auth-form-footer">
        {isLoginMode && (
          <div className="auth-forgot-link-wrap">
            <button
              type="button"
              className="auth-forgot-link"
              onClick={() => switchMode('forgot')}
              disabled={isLoading}
            >
              Забыли пароль?
            </button>
          </div>
        )}

        {isForgotMode ? (
          <button
            type="button"
            className="auth-switch-btn"
            onClick={() => switchMode('login')}
            disabled={isLoading}
          >
            Вернуться ко входу
          </button>
        ) : (
          <button
            type="button"
            className="auth-switch-btn"
            onClick={() => switchMode(isLoginMode ? 'register' : 'login')}
            disabled={isLoading}
          >
            {isLoginMode ? 'Нет аккаунта? Зарегистрироваться' : 'Уже есть аккаунт? Войти'}
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthForm;
